import useCustomToast from 'hooks/toast';
import useComprehensiveTopNav from 'hooks/useComprehensiveTopNav';
import Api from 'lib/api';
import { Button, Typography } from 'lib/components';
import { TextField } from 'lib/components/FormFields';
import React, { useState } from 'react';
import { useSelectAuthorization } from 'store';
import { PatientHit } from 'types';

const PatientsScreen: React.FC = () => {
	const toast = useCustomToast();
	const { canUpdateHospital } = useSelectAuthorization();
	const [query, setQuery] = useState('');
	const [by, setBy] = useState<'name' | 'id'>('name');
	const [patients, setPatients] = useState<PatientHit[]>([]);
	const [searching, setSearching] = useState(false);

	useComprehensiveTopNav('Patients');

	const search = async () => {
		if (query.trim().length < 2) {
			setPatients([]);
			return;
		}
		setSearching(true);
		const result = await Api.patients.search(query, undefined, by, 50);
		setSearching(false);
		if (result.error) {
			toast(result.error.message, 'error');
			return;
		}
		setPatients(result.data || []);
	};

	const remove = async (patient: PatientHit) => {
		if (patient.jobCount > 0) {
			toast('This patient has jobs and cannot be deleted.', 'error');
			return;
		}
		if (!window.confirm(`Delete ${patient.displayName}?`)) return;
		const result = await Api.patients.delete(patient.id);
		if (result.error) {
			toast(result.error.message, 'error');
			return;
		}
		setPatients((current) => current.filter((item) => item.id !== patient.id));
	};

	return (
		<div style={{ padding: '1.5rem' }}>
			<Typography>Search by last name, then first name, or by patient ID. This list does not add patients.</Typography>
			<div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginTop: '1rem' }}>
				<TextField
					label={by === 'id' ? 'Patient ID' : 'Patient name'}
					variant="labelled"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				<Button title={by === 'name' ? 'Search by ID' : 'Search by name'} variant="clear" onClick={() => setBy(by === 'name' ? 'id' : 'name')} />
				<Button title={searching ? 'Searching...' : 'Search'} disabled={searching} onClick={search} />
			</div>
			{patients.map((patient) => (
				<div
					key={patient.id}
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						padding: '0.75rem 0',
						borderBottom: '1px solid #eee',
					}}
				>
					<div>
						<Typography weight="bold">{patient.displayName}</Typography>
						<Typography>
							{patient.code} · {patient.hospitalName} · {patient.jobCount} jobs
						</Typography>
					</div>
					{canUpdateHospital && patient.jobCount === 0 && (
						<Button title="Delete" variant="clear" onClick={() => remove(patient)} />
					)}
				</div>
			))}
		</div>
	);
};

export default PatientsScreen;
